export type Meridiem = 'AM' | 'PM';

export type TwelveHourTimeParts = {
  hour: string;
  minute: string;
  meridiem: Meridiem | '';
};

const TIME_12_HOUR_PATTERN = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i;
const TIME_24_HOUR_PATTERN = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

const pad2 = (value: number): string => String(value).padStart(2, '0');

const isValidTime = (hour: number, minute: number): boolean =>
  Number.isInteger(hour) && Number.isInteger(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;

export const isValid12HourTime = (time?: string | null): boolean => {
  const value = (time || '').trim();
  const match = value.match(TIME_12_HOUR_PATTERN);

  if (!match) return false;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  return hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59;
};

export const isValidTimeValue = (time?: string | null): boolean => {
  const normalizedTime = normalizeTimeTo24Hour(time);
  const match = normalizedTime.match(TIME_24_HOUR_PATTERN);

  if (!match) return false;

  return isValidTime(Number(match[1]), Number(match[2]));
};

export const normalizeTimeTo24Hour = (time?: string | null): string => {
  const value = (time || '').trim();

  if (!value) return '';

  const twelveHourMatch = value.match(TIME_12_HOUR_PATTERN);
  if (twelveHourMatch) {
    const hour = Number(twelveHourMatch[1]);
    const minute = Number(twelveHourMatch[2]);
    const meridiem = twelveHourMatch[3].toUpperCase() as Meridiem;

    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return value;

    const hour24 = meridiem === 'AM'
      ? hour % 12
      : (hour % 12) + 12;

    return `${pad2(hour24)}:${pad2(minute)}`;
  }

  const twentyFourHourMatch = value.match(TIME_24_HOUR_PATTERN);
  if (twentyFourHourMatch) {
    const hour = Number(twentyFourHourMatch[1]);
    const minute = Number(twentyFourHourMatch[2]);

    if (!isValidTime(hour, minute)) return value;

    return `${pad2(hour)}:${pad2(minute)}`;
  }

  return value;
};

export const toApiTimeValue = (time?: string | null): string => {
  const normalizedTime = normalizeTimeTo24Hour(time);

  if (!normalizedTime) return normalizedTime;

  return TIME_24_HOUR_PATTERN.test(normalizedTime)
    ? `${normalizedTime}:00`
    : normalizedTime;
};

export const getTwelveHourTimeParts = (time?: string | null): TwelveHourTimeParts => {
  const normalizedTime = normalizeTimeTo24Hour(time);
  const match = normalizedTime.match(TIME_24_HOUR_PATTERN);

  if (!match) {
    return { hour: '', minute: '', meridiem: '' };
  }

  const hour24 = Number(match[1]);
  const minute = Number(match[2]);

  if (!isValidTime(hour24, minute)) {
    return { hour: '', minute: '', meridiem: '' };
  }

  const meridiem: Meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;

  return {
    hour: pad2(hour12),
    minute: pad2(minute),
    meridiem,
  };
};

export const formatCurrentTime12Hour = (): string => {
  const now = new Date();
  const hour24 = now.getHours();
  const meridiem: Meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;

  return `${pad2(hour12)}:${pad2(now.getMinutes())} ${meridiem}`;
};

export const normalizeMeridiemTimeInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 0) return '';
  if (digits.length === 1) {
    return Number(digits) <= 1 ? digits : `0${digits}`;
  }
  if (digits.length === 2) {
    const hour = Number(digits);
    return hour >= 1 && hour <= 12 ? digits : digits[0];
  }

  const hour = Number(digits.slice(0, 2));
  const minutePrefix = digits[2];
  if (hour < 1 || hour > 12 || Number(minutePrefix) > 5) {
    return digits.slice(0, 2);
  }

  if (digits.length === 3) return `${digits.slice(0, 2)}:${minutePrefix}`;

  const minute = Number(digits.slice(2));
  return minute <= 59 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : `${digits.slice(0, 2)}:${minutePrefix}`;
};

export const isValid12HourClockTime = (time?: string | null): boolean => {
  const match = (time || '').trim().match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  return hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59;
};

export const composeTimeFrom12HourParts = (
  hour: string,
  minute: string,
  meridiem: Meridiem | ''
): string => {
  if (!hour || !minute || !meridiem) return '';

  const hour12 = Number(hour);
  const minuteNumber = Number(minute);

  if (hour12 < 1 || hour12 > 12 || minuteNumber < 0 || minuteNumber > 59) return '';

  const hour24 = meridiem === 'AM'
    ? hour12 % 12
    : (hour12 % 12) + 12;

  return `${pad2(hour24)}:${pad2(minuteNumber)}`;
};

export const formatTime12Hour = (time?: string | null, fallback = '-'): string => {
  const parts = getTwelveHourTimeParts(time);

  if (!parts.hour || !parts.minute || !parts.meridiem) {
    return time || fallback;
  }

  return `${parts.hour}:${parts.minute} ${parts.meridiem}`;
};

export const getTimeMinutes = (time?: string | null): number | null => {
  const normalizedTime = normalizeTimeTo24Hour(time);
  const match = normalizedTime.match(TIME_24_HOUR_PATTERN);

  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (!isValidTime(hour, minute)) return null;

  return hour * 60 + minute;
};
